import { useState } from "react";
import AddressAutocomplete, { type RuianSuggestion } from "../components/AddressAutocomplete";
import { apiPost, ApiError } from "../lib/api";

type FormState = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  street: string;
  houseNumber: string;
  city: string;
  postalCode: string;
  ruianAddressId: number | null;
  latitude: number | null;
  longitude: number | null;
  consent: boolean;
};

const initial: FormState = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  street: "",
  houseNumber: "",
  city: "",
  postalCode: "",
  ruianAddressId: null,
  latitude: null,
  longitude: null,
  consent: false,
};

export default function Register() {
  const [form, setForm] = useState<FormState>(initial);
  const [addressQuery, setAddressQuery] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const onSelectAddress = (s: RuianSuggestion) => {
    setAddressQuery(s.label);
    setForm((f) => ({
      ...f,
      street: s.street,
      houseNumber: s.houseNumber,
      city: s.city,
      postalCode: s.postalCode,
      ruianAddressId: s.ruianId,
      latitude: s.latitude,
      longitude: s.longitude,
    }));
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!form.consent) {
      setError("Pro registraci je potřeba souhlas se zpracováním osobních údajů.");
      return;
    }
    if (!form.street || !form.city) {
      setError("Vyberte adresu z našeptávače.");
      return;
    }

    setSubmitting(true);
    try {
      await apiPost("/api/register", form);
      setSuccess(true);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError("Registrace se nezdařila.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="container">
        <div className="card">
          <h1>Děkujeme!</h1>
          <p>
            Na adresu <strong>{form.email}</strong> jsme odeslali ověřovací email. Klikněte na odkaz v
            emailu pro dokončení registrace.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        <h1>Registrace do CityIsNews</h1>
        <p className="muted">
          Zaregistrujte se a dostávejte důležité zprávy z vašeho města — push notifikace, SMS, email.
        </p>

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={onSubmit}>
          <div className="field-row">
            <div className="field">
              <label htmlFor="firstName">Jméno</label>
              <input
                id="firstName"
                value={form.firstName}
                onChange={(e) => update("firstName", e.target.value)}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="lastName">Příjmení</label>
              <input
                id="lastName"
                value={form.lastName}
                onChange={(e) => update("lastName", e.target.value)}
                required
              />
            </div>
          </div>

          <div className="field">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              required
            />
          </div>

          <div className="field">
            <label htmlFor="phone">Telefon</label>
            <input
              id="phone"
              type="tel"
              placeholder="+420 ..."
              value={form.phone}
              onChange={(e) => update("phone", e.target.value)}
              required
            />
          </div>

          <div className="field">
            <label htmlFor="address">Adresa (ulice, číslo, město)</label>
            <AddressAutocomplete
              id="address"
              value={addressQuery}
              onChange={setAddressQuery}
              onSelect={onSelectAddress}
            />
            {form.street && (
              <p className="muted" style={{ marginTop: 8 }}>
                {form.street} {form.houseNumber}, {form.postalCode} {form.city}
                {form.ruianAddressId && ` (RÚIAN: ${form.ruianAddressId})`}
              </p>
            )}
          </div>

          <div className="field">
            <label>
              <input
                type="checkbox"
                checked={form.consent}
                onChange={(e) => update("consent", e.target.checked)}
                style={{ width: "auto", marginRight: 8 }}
              />
              Souhlasím se zpracováním osobních údajů pro účely zasílání zpráv.
            </label>
          </div>

          <button type="submit" disabled={submitting}>
            {submitting ? "Odesílám..." : "Zaregistrovat"}
          </button>
        </form>
      </div>
    </div>
  );
}
